import { useState } from 'react';
import { Certificate } from '../../types';
import { ui } from '../../data/ui';
import { ScrollReveal } from './ScrollReveal';
import './CertificateCard.css';

interface CertificateCardProps {
  certificate: Certificate;
  index: number;
  onClick: (certificate: Certificate) => void;
}

const isPlaceholder = (value: string) => value.startsWith('[') || value.includes('placeholder');

export function CertificateCard({ certificate, index, onClick }: CertificateCardProps) {
  return (
    <ScrollReveal delay={index * 0.05}>
      <button
        type="button"
        className="certificate-card glass-card"
        onClick={() => onClick(certificate)}
      >
        <div className="certificate-image">
          {!isPlaceholder(certificate.image) ? (
            <img src={certificate.image} alt={certificate.name} loading="lazy" />
          ) : (
            <div className="placeholder-image certificate-placeholder">
              {ui.certifications.addCertificate}
            </div>
          )}
        </div>
        <div className="certificate-info">
          <h3 className="certificate-name">{certificate.name}</h3>
          <p className="certificate-issuer">{certificate.issuer}</p>
          <span className="certificate-date">{certificate.date}</span>
        </div>
      </button>
    </ScrollReveal>
  );
}

interface CertificateModalProps {
  certificate: Certificate | null;
  onClose: () => void;
}

export function CertificateModal({ certificate, onClose }: CertificateModalProps) {
  if (!certificate) return null;

  return (
    <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal-content glass-card" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label={ui.certifications.close}>
          ×
        </button>
        <div className="modal-image">
          {!isPlaceholder(certificate.image) ? (
            <img src={certificate.image} alt={certificate.name} />
          ) : (
            <div className="placeholder-image certificate-placeholder-large">
              {ui.certifications.addCertificateImage}
            </div>
          )}
        </div>
        <div className="modal-info">
          <h2>{certificate.name}</h2>
          <p><strong>{ui.certifications.issuer}</strong> {certificate.issuer}</p>
          <p><strong>{ui.certifications.date}</strong> {certificate.date}</p>
          {certificate.credentialId && !isPlaceholder(certificate.credentialId) && (
            <p><strong>{ui.certifications.credentialId}</strong> {certificate.credentialId}</p>
          )}
          {certificate.credentialUrl && !isPlaceholder(certificate.credentialUrl) && (
            <a href={certificate.credentialUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
              {ui.certifications.verify}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export function useCertificateModal() {
  const [selected, setSelected] = useState<Certificate | null>(null);
  return {
    selected,
    open: (cert: Certificate) => setSelected(cert),
    close: () => setSelected(null),
  };
}
